/** @jsx jsx */
import { Flex, Heading, Paragraph } from "@eworkit/iris";
import { FormikProps } from "formik";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";
import * as Yup from "yup";

import { FileUploadStateItem } from "@/components/Files/upload/fileUploadReducer";
import { VALIDATION_TYPES } from "@/components/Formik";
import { Attachment } from "@/data/attachments";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { JobApplication } from "@/data/job-applications/types/JobApplication";

import { useApplyWizardDraftUpdate } from "../_components/useApplyWizardDraftUpdate";
import { ApplyAttachmentsFormGroup } from "./ApplyAttachmentsFormGroup";
import { ApplyCvFormGroup } from "./ApplyCvFormGroup";

export interface ApplyFilesFormValues {
  cvs: FileUploadStateItem<Attachment>[];
  attachments: FileUploadStateItem<Attachment>[];
}

export const APPLY_FILES_DEFAULT_VALUES: ApplyFilesFormValues = {
  cvs: [],
  attachments: [],
};

export const APPLY_FILES_VALIDATION_SCHEMA = Yup.object().shape({
  cvs: Yup.array()
    .required(VALIDATION_TYPES.REQUIRED)
    .test({
      message: VALIDATION_TYPES.UPLOAD_PROGRESS,
      test: (value: FileUploadStateItem<Attachment>[]) => value.every(i => i.progress === 100),
    })
    .test({
      message: VALIDATION_TYPES.UPLOAD_ERRORS,
      test: (value: FileUploadStateItem<Attachment>[]) => value.every(i => !i.error),
    }),
  attachments: Yup.array()
    .test({
      message: VALIDATION_TYPES.UPLOAD_PROGRESS,
      test: (value: FileUploadStateItem<Attachment>[]) => value.every(i => i.progress === 100),
    })
    .test({
      message: VALIDATION_TYPES.UPLOAD_ERRORS,
      test: (value: FileUploadStateItem<Attachment>[]) => value.every(i => !i.error),
    }),
});

export const transformDraftWithFiles = (values: ApplyFilesFormValues) => (draft: JobApplication) => ({
  ...draft,
  consultantProfile: {
    ...draft.consultantProfile,
    cvs: values.cvs.filter(i => i.attachment).map(i => i.attachment!),
  },
  attachments: values.attachments.filter(i => i.attachment).map(i => i.attachment!),
});

interface ApplyFilesFormFieldsProps {
  formik: FormikProps<ApplyFilesFormValues>;
  originalProfile?: IConsultantProfile;
}

export const ApplyFilesFormFields: FC<ApplyFilesFormFieldsProps> = ({ formik, originalProfile }) => {
  const { t } = useTranslation("session");

  useApplyWizardDraftUpdate(transformDraftWithFiles(formik.values));

  return (
    <React.Fragment>
      <Flex flexDirection="column" gap="xxl">
        <Flex flexDirection="column" gap="sm">
          <Flex flexDirection="column">
            <Flex gap="xxs">
              <Heading size="h-md">{t("JOB_REQUESTS.APPLY.WIZARD.CV")}</Heading>
              <Paragraph size="p-md" color="r500">
                *
              </Paragraph>
            </Flex>
            <Paragraph size="p-md" color="n700">
              {t("JOB_REQUESTS.APPLY.WIZARD.CV_TEXT")}
            </Paragraph>
          </Flex>

          <ApplyCvFormGroup formik={formik} originalProfile={originalProfile} />
        </Flex>

        <Flex flexDirection="column" gap="sm">
          <Flex flexDirection="column">
            <Heading size="h-md">{t("JOB_REQUESTS.APPLY.WIZARD.OTHER_DOCUMENTS")}</Heading>
            <Paragraph size="p-md" color="n700">
              {t("JOB_REQUESTS.APPLY.WIZARD.OTHER_DOCUMENTS_TEXT")}
            </Paragraph>
          </Flex>

          <ApplyAttachmentsFormGroup formik={formik} originalProfile={originalProfile} />
        </Flex>
      </Flex>
    </React.Fragment>
  );
};
